import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import FormTextArea from '@/shared/components/forms/FormTextArea';
import Button from '@/shared/components/ui/Button';
import Modal from '@/shared/components/ui/Modals/Modal';

interface IMarkUnfitModalProps {
  isOpen: boolean;
  deviceName: string;
  orgName?: string | null;
  status?: string;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<boolean>;
}

interface IUnfitForm {
  reason: string;
}

const MarkUnfitModal = ({
  isOpen,
  deviceName,
  orgName,
  status,
  onClose,
  onSubmit,
}: IMarkUnfitModalProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<IUnfitForm>({ defaultValues: { reason: '' } });

  useEffect(() => {
    if (isOpen) {
      reset({ reason: '' });
    }
  }, [isOpen, reset]);

  const onValid = async (data: IUnfitForm) => {
    const ok = await onSubmit(data.reason.trim());
    if (ok) {
      onClose();
    }
  };

  const reservedLabel = status?.toLowerCase() === 'maintenance' ? 'reserved for' : 'rented to';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title='Mark Device as Unfit?' size='md'>
      <form onSubmit={handleSubmit(onValid)} className='p-6'>
        <p className='text-sm text-gray-500 mb-3'>
          Device: <strong>{deviceName}</strong>
        </p>
        {orgName && (
          <div className='mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
            This device is currently {reservedLabel} <strong>{orgName}</strong>. Marking it Unfit
            will remove it from this organization and end the active rental.
          </div>
        )}
        <FormTextArea
          name='reason'
          label='Reason'
          placeholder='Describe why this device is unfit for use...'
          register={register}
          errors={errors}
          rows={4}
          validation={{ required: 'A reason is required to mark this device Unfit' }}
        />
        <div className='flex justify-end gap-3 mt-6'>
          <Button type='button' variant='secondary' onClick={onClose}>
            Cancel
          </Button>
          <Button type='submit' variant='danger' isLoading={isSubmitting}>
            Mark as Unfit
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default MarkUnfitModal;
